import { useState } from "react";
import { Star, Trash2, Languages } from "lucide-react";
import { toast } from "sonner";
import { ISLANDS, ISLAND_BY_SLUG } from "../../lib/constants";
import { api } from "../../lib/api";

export const ArticlesTab = ({
  articles,
  articleIsland,
  setArticleIsland,
  onFeature,
  onDelete,
}) => {
  const [retranslateBusy, setRetranslateBusy] = useState(false);

  const onRetranslate = async () => {
    if (!window.confirm(
      "Re-run Claude on every article whose stored title still looks " +
      "non-English (Papiamento/Dutch/Spanish stragglers). Runs in the " +
      "background — refresh in a couple minutes to see updated titles. Continue?"
    )) return;
    setRetranslateBusy(true);
    try {
      await api.post("/admin/backfill-english-titles");
      toast.success(
        "Retranslate job started. Check backend logs or refresh in 1–2 min."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retranslate job failed to start");
    } finally {
      setRetranslateBusy(false);
    }
  };

  return (
  <div data-testid="admin-articles-view">
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">
        Filter by island:
      </span>
      {ISLANDS.map((i) => {
        const active = articleIsland === i.slug;
        return (
          <button
            key={i.slug}
            onClick={() => setArticleIsland(i.slug)}
            data-testid={`admin-island-filter-${i.slug}`}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              active
                ? "bg-[#0AADA0] text-white"
                : "bg-white text-[#6B7280] hover:text-[#1A1A1A]"
            }`}
          >
            <span className="mr-1">{i.flag}</span>{i.name}
          </button>
        );
      })}
      <div className="ml-auto">
        <button
          onClick={onRetranslate}
          disabled={retranslateBusy}
          data-testid="admin-retranslate-stragglers-btn"
          title="Re-run Claude on articles whose titles still look like Papiamento / Dutch / Spanish"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-[#E63946] text-[#E63946] hover:bg-[#E63946]/10 disabled:opacity-50 transition-colors"
        >
          <Languages size={12} />
          {retranslateBusy ? "Starting…" : "Retranslate stragglers"}
        </button>
      </div>
    </div>

    <div className="card-soft overflow-hidden" data-testid="admin-articles-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] bg-[#FAFAFA]">
            <th className="px-4 py-3">Article</th>
            <th className="px-4 py-3 hidden md:table-cell">Island</th>
            <th className="px-4 py-3 hidden md:table-cell">Category</th>
            <th className="px-4 py-3 hidden md:table-cell">Source</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((a) => (
            <tr key={a.id} className="border-t border-[#E5E5E5]" data-testid={`admin-article-row-${a.id}`}>
              <td className="px-4 py-3 max-w-md">
                <div className="text-[#1A1A1A] truncate">{a.title}</div>
                <div className="text-xs text-[#6B7280] truncate">{a.ai_summary}</div>
              </td>
              <td className="px-4 py-3 text-xs hidden md:table-cell">
                {ISLAND_BY_SLUG[a.island]?.flag} {ISLAND_BY_SLUG[a.island]?.name || a.island}
              </td>
              <td className="px-4 py-3 text-xs text-[#6B7280] hidden md:table-cell">{a.category}</td>
              <td className="px-4 py-3 text-xs text-[#6B7280] hidden md:table-cell">{a.source_name}</td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex gap-2">
                  <button
                    onClick={() => onFeature(a.id, a.featured)}
                    title={a.featured ? "Unpin" : "Pin as featured"}
                    className={`p-2 rounded-lg ${a.featured ? "bg-[#0AADA0] text-white" : "text-[#6B7280] hover:bg-[#0AADA0]/10 hover:text-[#0AADA0]"}`}
                    data-testid={`admin-feature-${a.id}`}
                  >
                    <Star size={14} fill={a.featured ? "white" : "none"} />
                  </button>
                  <button
                    onClick={() => onDelete(a.id)}
                    className="p-2 rounded-lg text-[#6B7280] hover:bg-[#E63946]/10 hover:text-[#E63946]"
                    data-testid={`admin-delete-${a.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};
